import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import "@/App.css";

// Components
import Sidebar, { ALL_NAV_ITEMS } from "@/components/Sidebar";
import SipPhone from "@/components/SipPhone";
import CallMethodDialog from "@/components/CallMethodDialog";
import IncomingCallAlert from "@/components/IncomingCallAlert";
import AppLockScreen from "@/components/AppLockScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bell } from "lucide-react";

// Utils
import { takePendingRefresh, closeRefreshNotifications } from "@/utils/pendingRefresh";

// Pages
import Dashboard from "@/pages/Dashboard";
import MemberDashboard from "@/pages/MemberDashboard";
import LiveCalls from "@/pages/LiveCalls";
import Response from "@/pages/Response";
import Incidents from "@/pages/Incidents";
import IncidentDetail from "@/pages/IncidentDetail";
import Investigations from "@/pages/Investigations";
import CallHistory from "@/pages/CallHistory";
import BuildingCodes from "@/pages/BuildingCodes";
import Patrol from "@/pages/Patrol";
import RollCall from "@/pages/RollCall";
import Units from "@/pages/Units";
import Dispatch from "@/pages/Dispatch";
import ProfilesAdmin from "@/pages/ProfilesAdmin";
// Perpetrators.js is now deprecated - using ProfilesAdmin with role-based features
import PR from "@/pages/PR";
import SettingsPage from "@/pages/Settings";
import Login from "@/pages/Login";
import NotificationSettings from "@/pages/NotificationSettings";
import NotificationHistory from "@/pages/NotificationHistory";
import Media from "@/pages/Media";
import Watermarking from "@/pages/Watermarking";
import RadioRecordings from "@/pages/RadioRecordings";
import Hotline from "@/pages/Hotline";
import Map from "@/pages/Map";
import EventOps from "@/pages/EventOps";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Current client version - UPDATE THIS WHEN DEPLOYING
const CLIENT_VERSION = "1.0.0";

// Configure axios to include session token from localStorage as fallback for iOS 26+ cookie issues
// This device, for Settings → Audit next to the IP address: a random ID made
// the first time the app runs here (kept until the app's data is cleared),
// the phone model where the browser will say it, and the installed app
// version. Only sent to our own /api.
const DEVICE_ID = (() => {
  try {
    let id = localStorage.getItem("cchd_device_id");
    if (!id) {
      id = `dev_${(crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}${Math.random()}`).replace(/[^a-zA-Z0-9]/g, "").slice(0, 24)}`;
      localStorage.setItem("cchd_device_id", id);
    }
    return id;
  } catch { return ""; }
})();
let DEVICE_MODEL = "";
try {
  // Chrome on Android no longer puts the model in the user agent; ask for it.
  navigator.userAgentData?.getHighEntropyValues?.(["model", "platformVersion"])
    .then((v) => { DEVICE_MODEL = [v.model, v.platform && v.platformVersion ? `${v.platform} ${v.platformVersion}` : ""].filter(Boolean).join(" · ").slice(0, 80); })
    .catch(() => {});
} catch { /* not offered */ }

axios.interceptors.request.use((config) => {
  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${sessionToken}`;
  }
  if (typeof config.url === "string" && config.url.includes("/api/")) {
    if (DEVICE_ID) config.headers["X-Device-Id"] = DEVICE_ID;
    if (DEVICE_MODEL) config.headers["X-Device-Model"] = DEVICE_MODEL;
    try {
      const apk = localStorage.getItem("installedApkVersion");
      if (apk) config.headers["X-App-Version"] = apk;
    } catch { /* storage off */ }
  }
  return config;
});

// Global 401 handler — triggers React auth state reset via custom event
// Debounced to avoid event spam from multiple stale 401s on Android resume
let _authExpiredTimeout = null;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      localStorage.removeItem('session_token');
      if (!_authExpiredTimeout) {
        window.dispatchEvent(new Event('auth-expired'));
        _authExpiredTimeout = setTimeout(() => { _authExpiredTimeout = null; }, 2000);
      }
    }
    return Promise.reject(error);
  }
);

// Keeps the localStorage copy of the session token in sync with the server.
// Image URLs carry ?token=<this> because <img> tags can't send the
// Authorization header above, and iOS/Android block our cross-site session
// cookie. The OAuth callback used to be the only writer, so any session
// restored from the cookie alone — or one whose copy the 401 handler cleared —
// left mobile with token-less image URLs that all 401. Desktop kept working
// because third-party cookies still ride along there.
// Returns the user object with the token stripped back out.
const rememberSessionToken = (data) => {
  if (!data) return data;
  const { session_token: token, ...user } = data;
  if (token) localStorage.setItem('session_token', token);
  return user;
};

// Helper function to get media URL with auth token for img tags (which don't send cookies)
// This ensures admin users see clean images without watermarks
// ALWAYS routes through backend API for proper watermark handling
const getAuthenticatedMediaUrl = (url) => {
  if (!url) return url;

  // Don't process data URLs
  if (url.startsWith('data:')) return url;

  const sessionToken = localStorage.getItem('session_token');

  // If the URL already points at our uploads endpoint (e.g. ".../uploads/<id>"
  // or ".../uploads/<id>/thumb?size=400"), keep its path + query exactly and
  // only append the token if missing. Stripping it down to a "filename" the
  // way we used to broke thumb URLs by reducing them to ".../uploads/thumb".
  const uploadsPathRe = /\/(?:api\/)?uploads\/[^/?#]+(?:\/[^?#]*)?(?:\?[^#]*)?$/;
  if (uploadsPathRe.test(url)) {
    // Absolutise first. /api/uploads/media-library hands back relative paths
    // like "/api/uploads/<id>", and a relative URL in an <img> or <video>
    // resolves against the FRONTEND origin — which is a different domain from
    // the API, so the request never reaches the backend and the element just
    // fails to load. Thumbnails were unaffected only because Media.js builds
    // their URLs absolutely itself.
    let absolute = url;
    if (!/^https?:\/\//i.test(url)) {
      absolute = `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
    }
    if (!sessionToken) return absolute;
    if (/[?&]token=/.test(absolute)) return absolute;
    return absolute.includes('?')
      ? `${absolute}&token=${sessionToken}`
      : `${absolute}?token=${sessionToken}`;
  }

  // Fallback: extract filename from any other URL format and build absolute
  // backend URL. Must be absolute because frontend and backend are on
  // different domains.
  let filename = url.split('/').pop().split('?')[0];
  const apiUrl = `${API}/uploads/${filename}`;

  if (sessionToken) {
    return `${apiUrl}?token=${sessionToken}`;
  }

  return apiUrl;
};

// Helper: VAPID base64url → Uint8Array for pushManager.subscribe
const vapidBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

// Creates a fresh push subscription against the current VAPID key and
// registers it with the backend. Used by the self-healing path when the
// browser has no subscription (token rotated / unsubscribed out-of-band).
const createAndRegisterPushSubscription = async (registration) => {
  const configResponse = await axios.get(`${API}/app-config`, { withCredentials: true });
  const vapidPublicKey = configResponse.data?.vapid_public_key;
  if (!vapidPublicKey) return false;

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: vapidBase64ToUint8Array(vapidPublicKey),
  });
  const subJson = subscription.toJSON();
  await axios.post(`${API}/push/subscribe`, {
    endpoint: subscription.endpoint,
    auth_key: subJson.keys.auth,
    p256dh_key: subJson.keys.p256dh,
  }, { withCredentials: true });
  return true;
};

// Helper function to validate and refresh push subscription.
// Self-heals three failure modes that affect iOS PWAs:
//   1) Backend says not subscribed but browser has subscription  → re-POST to backend.
//   2) Backend says not subscribed AND browser has no subscription → subscribe fresh.
//   3) Browser subscription exists but backend not queried yet → verify then heal.
const validateAndRefreshPushSubscription = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  // Respect explicit user opt-out from Settings.
  if (localStorage.getItem('push_opted_out') === '1') return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const existingSub = await registration.pushManager.getSubscription();

    let backendSubscribed = null;
    try {
      const response = await axios.get(`${API}/push/status`, { withCredentials: true });
      backendSubscribed = !!response.data?.is_subscribed;
    } catch (err) {
      if (err.response?.status === 401) return; // not logged in
      console.error('Error checking push status:', err);
      return;
    }

    if (backendSubscribed && existingSub) return;

    if (!existingSub) {
      // Browser has no subscription (iOS rotated the token or user cleared it).
      // Subscribe fresh and register with backend.
      try {
        await createAndRegisterPushSubscription(registration);
      } catch (err) {
        console.error('Self-heal subscribe failed:', err);
      }
      return;
    }

    // We have a browser subscription but backend doesn't know about it.
    const p256dh = existingSub.getKey('p256dh');
    const auth = existingSub.getKey('auth');
    if (!p256dh || !auth) {
      await existingSub.unsubscribe();
      try { await createAndRegisterPushSubscription(registration); } catch (_) { /* ignore */ }
      return;
    }
    const p256dhKey = btoa(String.fromCharCode.apply(null, new Uint8Array(p256dh)));
    const authKey = btoa(String.fromCharCode.apply(null, new Uint8Array(auth)));
    try {
      await axios.post(`${API}/push/subscribe`, {
        endpoint: existingSub.endpoint,
        p256dh_key: p256dhKey,
        auth_key: authKey,
      }, { withCredentials: true });
    } catch (err) {
      console.error('Error re-registering push subscription:', err);
    }
  } catch (err) {
    console.error('Error validating push subscription:', err);
  }
};

// Auth Context
const AuthContext = createContext(null);

// Settings Context for app-wide settings like theme and font size
const SettingsContext = createContext(null);

// Location Sharing Context for user-initiated location sharing that persists across navigation
const LocationSharingContext = createContext(null);

// SIP Phone Context for global phone access
const SipPhoneContext = createContext(null);

// App Lock Context for app-wide lock status
const AppLockContext = createContext(null);

// Shared Data Context - caches common API data so pages don't re-fetch
const SharedDataContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useSettings = () => useContext(SettingsContext);
export const useLocationSharing = () => useContext(LocationSharingContext);
export const useSipPhone = () => useContext(SipPhoneContext);
export const useAppLock = () => useContext(AppLockContext);
export const useSharedData = () => useContext(SharedDataContext);

// Apply an admin Refresh that we couldn't deliver by push — the iPhone path,
// and any device that was closed when the button was pressed. Runs off the
// settings sync so it costs no extra request.
const applyRequestedRefresh = (requestedAt) => {
  if (!requestedAt) return;
  const applied = localStorage.getItem('lastAppliedRefresh');
  if (applied === requestedAt) return;
  // Record before reloading, or the fresh page sees the same value and loops.
  localStorage.setItem('lastAppliedRefresh', requestedAt);
  if (!applied) return;  // First sight on this device — nothing to catch up on.
  console.log('Applying admin-requested refresh from', requestedAt);
  window.location.reload();
};

// Sync notification settings from server to local storage
const syncNotificationSettings = async () => {
  try {
    const response = await axios.get(`${API}/users/me/notifications`, { withCredentials: true });
    if (response.data) {
      // Get existing local settings
      const saved = localStorage.getItem('notificationSettings');
      let localSettings = {};
      if (saved) {
        try {
          localSettings = JSON.parse(saved);
        } catch (e) {}
      }
      
      // Merge server settings (overwrite notification type preferences)
      const serverSettings = response.data;
      const newSettings = {
        ...localSettings,
        enabled: serverSettings.enabled,
        normalCalls: serverSettings.normalCalls,
        priorityCalls: serverSettings.priorityCalls,
        priorityCallUpdates: serverSettings.priorityCallUpdates,
        transmitted1085: serverSettings.transmitted1085,
        transmitted1013: serverSettings.transmitted1013,
        callUpdates: serverSettings.callUpdates,
        patrolActivity: serverSettings.patrolActivity,
        // Admin's absolute off switch — the service worker honours this even
        // for 10-85/10-13 and master broadcasts.
        allEnabled: serverSettings.allEnabled,
        // Server is authoritative for mute. Without this the admin's Mute and
        // Unmute never reached the device at all: mutedUntil only ever came
        // from whatever this phone had cached locally.
        mutedUntil: serverSettings.mutedUntil
      };
      
      // Save to localStorage
      localStorage.setItem('notificationSettings', JSON.stringify(newSettings));
      
      // Save to IndexedDB for service worker
      if ('indexedDB' in window) {
        const request = indexedDB.open('CHSPSettings', 2);
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('settings')) {
            db.createObjectStore('settings', { keyPath: 'id' });
          }
        };
        request.onsuccess = (event) => {
          const db = event.target.result;
          if (db.objectStoreNames.contains('settings')) {
            const transaction = db.transaction(['settings'], 'readwrite');
            const store = transaction.objectStore('settings');
            store.put({ id: 'notificationSettings', data: newSettings });
          }
        };
      }

      // Last, so the settings above are already persisted if this reloads us.
      applyRequestedRefresh(response.data.refreshRequestedAt);
    }
  } catch (error) {
    // Silently fail - user may not be logged in yet
  }
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        timeout: 8000
      });
      setUser(rememberSessionToken(response.data));
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    // The server has ended the session (cookie or Bearer); drop our copy too.
    try { localStorage.removeItem('session_token'); } catch { /* storage off */ }
    setUser(null);
  };

  // Re-check auth and reload when app resumes from background after extended period
  useEffect(() => {
    // Persist timestamp so it survives page freezing on iOS
    const LAST_VISIBLE_KEY = '__app_last_visible';
    const getLastVisible = () => {
      try { return parseInt(sessionStorage.getItem(LAST_VISIBLE_KEY)) || Date.now(); } catch { return Date.now(); }
    };
    const setLastVisible = () => {
      try { sessionStorage.setItem(LAST_VISIBLE_KEY, String(Date.now())); } catch {}
    };
    setLastVisible(); // Initialize on mount

    // Prevent multiple concurrent resume calls (Android fires visibilitychange + focus together)
    let resuming = false;
    const handleResume = async () => {
      if (resuming) return;
      resuming = true;
      try {
        const elapsed = Date.now() - getLastVisible();
        // If app was in background for more than 20 minutes, verify session and reload
        if (elapsed > 20 * 60 * 1000) {
          try {
            const response = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 8000 });
            if (response.data) {
              // Re-seed the token before reloading so images work on the way back up.
              rememberSessionToken(response.data);
              // Don't setUser before reload — it triggers lock check which shows fingerprint,
              // then reload shows it again. The reload's checkAuth will handle user state.
              window.location.reload();
            }
          } catch (err) {
            if (err.response?.status === 401) {
              setUser(null);
            }
          }
        } else if (elapsed > 30000) {
          // Soft re-check: only log out on confirmed 401, not network errors
          try {
            const response = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 8000 });
            if (response.data) setUser(rememberSessionToken(response.data));
          } catch (err) {
            if (err.response?.status === 401) setUser(null);
          }
        }
      } finally {
        resuming = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleResume();
      } else {
        setLastVisible();
      }
    };

    // iOS Safari: when page is restored from back-forward cache after being frozen
    const handlePageShow = (e) => {
      if (e.persisted) {
        handleResume();
      }
    };

    // Additional fallback for window focus (some mobile browsers)
    const handleFocus = () => {
      const elapsed = Date.now() - getLastVisible();
      if (elapsed > 20 * 60 * 1000) {
        handleResume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    checkAuth();

    // On auth-expired, verify session before logging out — stale 401s from
    // background requests on Android resume should not force-logout the user
    let verifying = false;
    const handleAuthExpired = async () => {
      if (verifying) return;
      verifying = true;
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          timeout: 8000
        });
        if (response.data) {
          setUser(response.data);
        } else {
          setUser(null);
        }
      } catch (err) {
        // Only log out on confirmed 401 — network errors/timeouts should not force logout
        if (err.response?.status === 401) {
          setUser(null);
        }
      } finally {
        verifying = false;
      }
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, []);

  // Only run auth-dependent background tasks when logged in
  useEffect(() => {
    if (!user) return;

    syncNotificationSettings();
    validateAndRefreshPushSubscription();

    const pushValidationInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        validateAndRefreshPushSubscription();
      }
    }, 5 * 60 * 1000);

    const notificationSyncInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        syncNotificationSettings();
      }
    }, 60 * 1000);

    // Sync the moment we come back to the foreground rather than waiting out
    // the interval. This is what makes an admin Refresh land promptly on an
    // iPhone, where the app is suspended and can't be pushed a silent reload.
    const handleForeground = () => {
      if (document.visibilityState === 'visible') {
        syncNotificationSettings();
      }
    };
    document.addEventListener('visibilitychange', handleForeground);

    return () => {
      clearInterval(pushValidationInterval);
      clearInterval(notificationSyncInterval);
      document.removeEventListener('visibilitychange', handleForeground);
    };
  }, [user]);

  // Service worker message listener (mount once, doesn't need auth)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      let customRingtoneAudio = null;

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'GET_NOTIFICATION_SETTINGS') {
          const settings = localStorage.getItem('notificationSettings');
          const parsedSettings = settings ? JSON.parse(settings) : {
            enabled: true,
            normalCalls: true,
            priorityCalls: true,
            priorityCallUpdates: true,
            transmitted1085: true,
            transmitted1013: true,
            callUpdates: true,
            sound: true
          };
          event.ports[0].postMessage(parsedSettings);
        } else if (event.data?.type === 'PLAY_CUSTOM_RINGTONE' || event.data?.type === 'PLAY_PRESET_RINGTONE') {
          const ringtoneUrl = event.data.ringtoneUrl || event.data.ringtone;
          if (customRingtoneAudio) {
            customRingtoneAudio.pause();
            customRingtoneAudio.currentTime = 0;
          }
          if (ringtoneUrl) {
            customRingtoneAudio = new Audio(ringtoneUrl);
            customRingtoneAudio.play().catch(err => {
              console.error('Error playing ringtone:', err);
            });
          }
        } else if (event.data?.type === 'SW_KEEPALIVE') {
          validateAndRefreshPushSubscription().catch(err => {
            console.log('Keep-alive validation error:', err);
          });
        }
      });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Get the authorization code from URL params
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    
    if (code) {
      // OAuth state must match the one Login.js kept in this browser (15 min),
      // or this sign-in wasn't started here — a crafted link signing this
      // browser in to someone else's account. Used once.
      let stored = null;
      try { stored = JSON.parse(localStorage.getItem("oauth_state") || "null"); localStorage.removeItem("oauth_state"); } catch { /* private mode */ }
      const stateOk = stored && stored.v && stored.v === params.get("state") && Date.now() - (stored.t || 0) < 15 * 60 * 1000;
      if (!stateOk) {
        toast.error("That sign-in link expired — please sign in again");
        navigate("/login", { replace: true });
        return;
      }
      // Exchange code for session via our backend
      axios.post(`${API}/auth/google/callback`, { code, origin: window.location.origin }, { withCredentials: true })
        .then(response => {
          rememberSessionToken(response.data);
          login(response.data.user);
          toast.success("Welcome back!");
          navigate("/dashboard", { replace: true, state: { user: response.data.user } });
        })
        .catch(error => {
          console.error("Auth error:", error);
          toast.error("Authentication failed");
          navigate("/login", { replace: true });
        });
    } else {
      navigate("/login", { replace: true });
    }
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-slate-400">Authenticating...</p>
      </div>
    </div>
  );
};

// PIN Verification Component
const PinVerification = ({ onVerified }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length < 4) {
      setError("PIN must be at least 4 digits");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/users/me/verify-pin`, { pin_code: pin }, { withCredentials: true });
      toast.success("PIN verified");
      onVerified();
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800">
        <CardHeader className="text-center">
          <CardTitle className="text-xl text-blue-400">Security PIN Required</CardTitle>
          <p className="text-sm text-slate-400 mt-2">Please enter your PIN to continue</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">PIN Code</Label>
              <input type="text" name="fake_field" autoComplete="off" style={{ display: 'none' }} tabIndex={-1} />
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="security-pin-entry"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore
                data-form-type="other"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Enter PIN..."
                className="bg-slate-800 border-slate-700 text-center text-2xl tracking-[0.5em] font-mono"
                style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' }}
                autoFocus
                maxLength={8}
              />
            </div>
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading || pin.length < 4}
            >
              {loading ? "Verifying..." : "Verify PIN"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin PIN Verification Component
const AdminPinVerification = ({ onVerified, message }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length < 4) {
      setError("PIN must be at least 4 digits");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/users/me/verify-admin-pin`, { pin_code: pin }, { withCredentials: true });
      toast.success("Access granted");
      onVerified();
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900 border-purple-800 border-2">
        <CardHeader className="text-center">
          <CardTitle className="text-xl text-purple-400">Access PIN Required</CardTitle>
          <p className="text-sm text-slate-400 mt-2">
            {message || "Please enter the access PIN to continue"}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Access PIN</Label>
              <input type="text" name="fake_field" autoComplete="off" style={{ display: 'none' }} tabIndex={-1} />
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="access-pin-entry"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore
                data-form-type="other"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Enter PIN..."
                className="bg-slate-800 border-slate-700 text-center text-2xl tracking-[0.5em] font-mono"
                style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' }}
                autoFocus
                maxLength={8}
              />
            </div>
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            <Button 
              type="submit" 
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={loading || pin.length < 4}
            >
              {loading ? "Verifying..." : "Verify Access"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requiredPermission = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [pinStatus, setPinStatus] = useState({ checked: false, required: false, verified: true });
  const [adminPinStatus, setAdminPinStatus] = useState({ checked: false, required: false, verified: true, message: "" });
  const [appLockStatus, setAppLockStatus] = useState({
    checked: false,
    enabled: false,
    locked: false,
    scope: "app",
    lockedPages: [],
    timeoutMinutes: 5,
    biometricRegistered: false
  });
  const appLockCheckIntervalRef = useRef(null);
  const heartbeatThrottleRef = useRef(0);

  // Check PIN status when user is loaded
  useEffect(() => {
    const checkPinStatus = async () => {
      if (!user) return;

      try {
        const response = await axios.get(`${API}/users/me/pin-status`, { withCredentials: true });
        setPinStatus({
          checked: true,
          required: response.data.pin_required,
          verified: response.data.pin_verified
        });
      } catch (err) {
        console.error("Failed to check PIN status:", err);
        setPinStatus({ checked: true, required: false, verified: true });
      }
    };

    if (user && !pinStatus.checked) {
      checkPinStatus();
    }
  }, [user, pinStatus.checked]);

  // Check Admin PIN status when user is loaded
  useEffect(() => {
    const checkAdminPinStatus = async () => {
      if (!user) return;

      try {
        const response = await axios.get(`${API}/users/me/admin-pin-status`, { withCredentials: true });
        setAdminPinStatus({
          checked: true,
          required: response.data.admin_pin_required,
          verified: response.data.admin_pin_verified,
          message: response.data.admin_pin_message || ""
        });
      } catch (err) {
        console.error("Failed to check admin PIN status:", err);
        setAdminPinStatus({ checked: true, required: false, verified: true, message: "" });
      }
    };

    if (user && !adminPinStatus.checked) {
      checkAdminPinStatus();
    }
  }, [user, adminPinStatus.checked]);

  // Check App Lock status when user is loaded
  const checkAppLockStatus = useCallback(async () => {
    if (!user) return;

    try {
      const response = await axios.get(`${API}/users/me/app-lock-status`, { withCredentials: true });
      setAppLockStatus({
        checked: true,
        enabled: response.data.enabled,
        locked: response.data.locked,
        scope: response.data.scope || "app",
        lockedPages: response.data.locked_pages || [],
        timeoutMinutes: response.data.timeout_minutes || 5,
        biometricRegistered: response.data.biometric_registered || false
      });
    } catch (err) {
      console.error("Failed to check app lock status:", err);
      setAppLockStatus({ checked: true, enabled: false, locked: false, scope: "app", lockedPages: [], timeoutMinutes: 5, biometricRegistered: false });
    }
  }, [user]);

  useEffect(() => {
    if (user && !appLockStatus.checked) {
      checkAppLockStatus();
    }
  }, [user, appLockStatus.checked, checkAppLockStatus]);

  // Periodic app lock status check (every 30 seconds)
  useEffect(() => {
    if (!user || !appLockStatus.enabled) return;

    appLockCheckIntervalRef.current = setInterval(() => {
      checkAppLockStatus();
    }, 30000);

    return () => {
      if (appLockCheckIntervalRef.current) {
        clearInterval(appLockCheckIntervalRef.current);
      }
    };
  }, [user, appLockStatus.enabled, checkAppLockStatus]);

  // Activity tracking for app lock heartbeat
  useEffect(() => {
    if (!user || !appLockStatus.enabled || appLockStatus.locked) return;

    const sendHeartbeat = async () => {
      const now = Date.now();
      // Throttle heartbeat to max once per 30 seconds
      if (now - heartbeatThrottleRef.current < 30000) return;
      heartbeatThrottleRef.current = now;

      try {
        await axios.post(`${API}/users/me/app-lock-heartbeat`, {}, { withCredentials: true });
      } catch (err) {
        // Silently fail
      }
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, sendHeartbeat, { passive: true }));

    return () => {
      events.forEach(e => window.removeEventListener(e, sendHeartbeat));
    };
  }, [user, appLockStatus.enabled, appLockStatus.locked]);

  // Log page visits (only when user is authenticated and verified)
  useEffect(() => {
    if (user && pinStatus.verified && adminPinStatus.verified && !appLockStatus.locked) {
      const pageName = location.pathname.replace(/^\//, '') || 'home';
      // Don't await - fire and forget
      axios.post(`${API}/audit/log-page-visit`, { page: pageName }, { withCredentials: true })
        .catch(() => {}); // Silently ignore errors
    }
  }, [location.pathname, user, pinStatus.verified, adminPinStatus.verified, appLockStatus.locked]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check device access restriction
  if (user.device_access && user.device_access !== "all") {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      || (navigator.maxTouchPoints > 0 && window.innerWidth < 1024);
    const isDesktop = !isMobile;

    if (user.device_access === "mobile_only" && isDesktop) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-900 border-red-800 border-2">
            <CardHeader className="text-center">
              <CardTitle className="text-xl text-red-400">Access Restricted</CardTitle>
              <p className="text-sm text-slate-400 mt-2">
                Your account is restricted to mobile devices only. Please use a mobile device to access this application.
              </p>
            </CardHeader>
          </Card>
        </div>
      );
    }

    if (user.device_access === "desktop_only" && isMobile) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-900 border-red-800 border-2">
            <CardHeader className="text-center">
              <CardTitle className="text-xl text-red-400">Access Restricted</CardTitle>
              <p className="text-sm text-slate-400 mt-2">
                Your account is restricted to desktop devices only. Please use a desktop computer to access this application.
              </p>
            </CardHeader>
          </Card>
        </div>
      );
    }
  }

  // Check PIN verification (Google login PIN)
  if (!pinStatus.checked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (pinStatus.required && !pinStatus.verified) {
    return (
      <PinVerification 
        onVerified={() => setPinStatus({ ...pinStatus, verified: true })} 
      />
    );
  }

  // Check Admin PIN verification (separate from Google login)
  if (!adminPinStatus.checked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (adminPinStatus.required && !adminPinStatus.verified) {
    return (
      <AdminPinVerification
        onVerified={() => setAdminPinStatus({ ...adminPinStatus, verified: true })}
        message={adminPinStatus.message}
      />
    );
  }

  // Check App Lock status
  if (!appLockStatus.checked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  // Check if app lock applies
  if (appLockStatus.enabled && appLockStatus.locked) {
    // Find the current page key
    const currentNavItem = ALL_NAV_ITEMS.find(item =>
      location.pathname === item.path || location.pathname.startsWith(item.path + "/")
    );
    const currentPageKey = currentNavItem?.page_key;

    // Check scope - if "app" scope, always show lock screen
    // If "pages" scope, only show lock screen for specific pages
    const shouldShowLock = appLockStatus.scope === "app" ||
      (appLockStatus.scope === "pages" && currentPageKey && appLockStatus.lockedPages.includes(currentPageKey));

    if (shouldShowLock) {
      return (
        <AppLockScreen
          onUnlock={() => {
            setAppLockStatus({ ...appLockStatus, locked: false });
            checkAppLockStatus(); // Refresh status from server
          }}
          pageKey={appLockStatus.scope === "pages" ? currentPageKey : null}
          biometricRegistered={appLockStatus.biometricRegistered}
        />
      );
    }
  }

  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/live-calls" replace />;
  }

  // Response users can ONLY access Response and Notification Settings pages
  if (user.role === "response") {
    const allowedPaths = ["/response", "/notification-settings"];
    const isAllowed = allowedPaths.some(path => location.pathname.startsWith(path));
    if (!isAllowed) {
      return <Navigate to="/response" replace />;
    }
  }

  // Response + History users can access Response, Call History, and Notification Settings
  if (user.role === "response_history") {
    const allowedPaths = ["/response", "/call-history", "/notification-settings"];
    const isAllowed = allowedPaths.some(path => location.pathname.startsWith(path));
    if (!isAllowed) {
      return <Navigate to="/response" replace />;
    }
  }

  // History Only users can ONLY access Call History and Notification Settings
  if (user.role === "history_only") {
    const allowedPaths = ["/call-history", "/notification-settings"];
    const isAllowed = allowedPaths.some(path => location.pathname.startsWith(path));
    if (!isAllowed) {
      return <Navigate to="/call-history" replace />;
    }
  }

  // Live Calls Only users can only access Live Calls
  if (user.role === "live_calls_only" && !location.pathname.startsWith("/live-calls")) {
    return <Navigate to="/live-calls" replace />;
  }

  // Operator role can access Live Calls and Incidents
  if (user.role === "operator") {
    const allowedPaths = ["/live-calls", "/incidents"];
    const isAllowed = allowedPaths.some(path => location.pathname.startsWith(path));
    if (!isAllowed) {
      return <Navigate to="/live-calls" replace />;
    }
  }

  // Check required permission
  if (requiredPermission && user.role !== "admin") {
    const userPermissions = user.permissions || [];
    if (!userPermissions.includes(requiredPermission)) {
      return <Navigate to="/member-dashboard" replace />;
    }
  }

  // Check page_access for custom role users (or any user with page_access set)
  // If page_access is defined (even as empty array), enforce it
  const hasPageAccessDefined = Array.isArray(user.page_access);
  const pageAccess = user.page_access || [];

  if (user.role !== "admin" && hasPageAccessDefined) {
    // Find the page_key for the current path
    const currentNavItem = ALL_NAV_ITEMS.find(item =>
      location.pathname === item.path || location.pathname.startsWith(item.path + "/")
    );

    // Always allow notification-settings and dispatch (utility pages)
    const alwaysAllowedPaths = ["/notification-settings", "/dispatch"];
    const isUtilityPage = alwaysAllowedPaths.some(path => location.pathname.startsWith(path));

    if (currentNavItem && !isUtilityPage) {
      const hasAccess = pageAccess.includes(currentNavItem.page_key);
      if (!hasAccess) {
        // Redirect to first allowed page, or notification-settings if none allowed
        const firstAllowedItem = ALL_NAV_ITEMS.find(item => pageAccess.includes(item.page_key));
        const redirectPath = firstAllowedItem ? firstAllowedItem.path : "/notification-settings";
        return <Navigate to={redirectPath} replace />;
      }
    }
  }

  return children;
};

// Default redirect component - redirects to first accessible page
const DefaultRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const pageAccess = user.page_access || [];

  // Dispatch users (and all admins) → live calls
  if (user.role === "admin" || pageAccess.includes("live_calls")) {
    return <Navigate to="/live-calls" replace />;
  }

  // Response users → response
  if (pageAccess.includes("response")) {
    return <Navigate to="/response" replace />;
  }

  // Fallback — first allowed page
  const firstAllowedItem = ALL_NAV_ITEMS.find(item => pageAccess.includes(item.page_key));
  if (firstAllowedItem) {
    return <Navigate to={firstAllowedItem.path} replace />;
  }
  return <Navigate to="/member-dashboard" replace />;
};

// Navigation items - moved to Sidebar component

// Main Layout Component with Sidebar
const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => {
    // Initialize theme from localStorage and apply to DOM
    const savedTheme = localStorage.getItem('theme') || 'dark';
    const validThemes = ['light', 'tactical', 'dark'];
    const resolvedTheme = validThemes.includes(savedTheme) ? savedTheme : 'dark';
    const savedCadMode = localStorage.getItem('cadMode') === 'true';
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('light', 'dark', 'tactical');
      document.documentElement.classList.add(resolvedTheme);
      if (resolvedTheme === 'tactical') document.documentElement.classList.add('dark');
      if (savedCadMode) document.documentElement.classList.add('cad');
      const themeColors = { light: '#f8fafc', tactical: '#162040', dark: '#0f172a' };
      const themeMeta = document.querySelector('meta[name="theme-color"]');
      if (themeMeta) themeMeta.setAttribute('content', savedCadMode ? '#0a0f1a' : (themeColors[resolvedTheme] || '#0f172a'));
      const schemeMeta = document.querySelector('meta[name="color-scheme"]');
      if (schemeMeta) schemeMeta.setAttribute('content', resolvedTheme === 'light' ? 'light' : 'dark');
    }
    return resolvedTheme;
  });
  const [cadMode, setCadMode] = useState(() => localStorage.getItem('cadMode') === 'true');
  const isDark = theme !== 'light';

  // Enforce allowed themes per user (admins default to all, others default to light+tactical)
  useEffect(() => {
    if (user) {
      const allowed = user.allowed_themes || (user.role === 'admin' ? ['dark', 'light', 'tactical'] : ['light', 'tactical']);
      if (!allowed.includes(theme)) {
        const fallback = allowed[0] || 'light';
        setTheme(fallback);
        localStorage.setItem('theme', fallback);
        document.documentElement.classList.remove('light', 'dark', 'tactical');
        document.documentElement.classList.add(fallback);
        if (fallback === 'tactical') document.documentElement.classList.add('dark');
        const themeMeta = document.querySelector('meta[name="theme-color"]');
        if (themeMeta) themeMeta.setAttribute('content', fallback === 'light' ? '#f8fafc' : fallback === 'tactical' ? '#162040' : '#0f172a');
        const schemeMeta = document.querySelector('meta[name="color-scheme"]');
        if (schemeMeta) schemeMeta.setAttribute('content', fallback === 'light' ? 'light' : 'dark');
      }
    }
  }, [user, theme]);

  const [fontSize, setFontSize] = useState(() => {
    return localStorage.getItem('fontSize') || 'normal';
  });
  const [notificationCount, setNotificationCount] = useState(0);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const updateCheckRef = useRef(null);
  const hasShownUpdateToast = useRef(false);
  
  // Background location tracking state (unit-based, admin-initiated)
  const [isLocationTracking, setIsLocationTracking] = useState(false);
  const [trackingCallAddress, setTrackingCallAddress] = useState(null);
  const [trackingCallId, setTrackingCallId] = useState(null);
  const locationWatchIdRef = useRef(null);
  const trackingCheckIntervalRef = useRef(null);
  const isLocationTrackingRef = useRef(false); // Ref to avoid stale closure
  const lastLocationUpdateRef = useRef(0); // Throttle location updates
  const LOCATION_UPDATE_INTERVAL = 5000; // Only send updates every 5 seconds

  // User-initiated location sharing state (persists across page navigation)
  const [userLocationSharing, setUserLocationSharing] = useState(false);
  const [userSharingCallId, setUserSharingCallId] = useState(null);
  const [userSharingAccuracy, setUserSharingAccuracy] = useState(null);
  const userLocationWatchRef = useRef(null);
  const userLocationIntervalRef = useRef(null);
  const userLastPositionRef = useRef(null);
  const userLastSendTimeRef = useRef(0);
  const userLocationSharingRef = useRef(false); // Ref to avoid stale closure
  const wakeLockRef = useRef(null); // Wake lock to prevent sleep during location sharing

  // SIP Phone state
  const [sipPhoneNumber, setSipPhoneNumber] = useState("");
  const [showCallMethodDialog, setShowCallMethodDialog] = useState(false);
  const [pendingCallNumber, setPendingCallNumber] = useState("");
  const [sipCallActive, setSipCallActive] = useState(false);
  const [sipRegistered, setSipRegistered] = useState(false);
  const [sipConnecting, setSipConnecting] = useState(false);
  const [callMyPhoneConfirmNumber, setCallMyPhoneConfirmNumber] = useState("");
  const sipPhoneRef = useRef(null);

  // Handle phone click - shows dialog if phone_callback_enabled, otherwise opens native dialer
  const handlePhoneClick = useCallback((phoneNumber) => {
    if (!phoneNumber) return;

    const cleanNumber = phoneNumber.replace(/\D/g, "");

    if (user?.phone_callback_enabled) {
      // Show choice dialog
      setPendingCallNumber(cleanNumber);
      setShowCallMethodDialog(true);
    } else {
      // Default to native dialer
      window.location.href = `tel:${cleanNumber}`;
    }
  }, [user?.phone_callback_enabled]);

  // Open native dialer
  const handleNativeDialer = useCallback(() => {
    if (pendingCallNumber) {
      window.location.href = `tel:${pendingCallNumber}`;
    }
  }, [pendingCallNumber]);

  // Use dispatch phone
  const handleDispatchPhone = useCallback(() => {
    if (pendingCallNumber) {
      setSipPhoneNumber(pendingCallNumber);
      // Also directly expand in case the number is the same (useEffect won't fire)
      sipPhoneRef.current?.expand();
    }
  }, [pendingCallNumber]);

  // Call My Phone - show confirmation first
  const handleCallMyPhone = useCallback(() => {
    if (!pendingCallNumber) return;
    setCallMyPhoneConfirmNumber(pendingCallNumber);
  }, [pendingCallNumber]);

  // Actually execute the Call My Phone after confirmation
  const executeCallMyPhone = useCallback(async () => {
    const number = callMyPhoneConfirmNumber;
    setCallMyPhoneConfirmNumber("");
    if (!number) return;
    try {
      toast.info("Calling your phone...");
      const chaverimMode = (() => {
        try { return sessionStorage.getItem('dialer.chaverim_mode') === '1'; } catch { return false; }
      })();
      const payload = { destination_number: number };
      if (chaverimMode) payload.caller_id_override = "+17182221800";
      await axios.post(`${API}/voice/call-my-phone`, payload, { withCredentials: true });
      toast.success("Answer your phone to connect.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to initiate call";
      toast.error(msg);
    }
  }, [callMyPhoneConfirmNumber]);

  // Keep refs in sync with state
  useEffect(() => {
    isLocationTrackingRef.current = isLocationTracking;
  }, [isLocationTracking]);

  useEffect(() => {
    userLocationSharingRef.current = userLocationSharing;
  }, [userLocationSharing]);

  // Request wake lock to prevent device from sleeping during location sharing
  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Wake lock acquired for location sharing');
        
        // Re-acquire wake lock if released (e.g., tab becomes inactive then active again)
        wakeLockRef.current.addEventListener('release', () => {
          console.log('Wake lock released');
          // Try to re-acquire if still sharing
          if (userLocationSharingRef.current) {
            requestWakeLock();
          }
        });
      } catch (err) {
        console.log('Wake lock request failed:', err.message);
      }
    } else {
      console.log('Wake Lock API not supported');
    }
  }, []);

  // Release wake lock
  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
      console.log('Wake lock released manually');
    }
  }, []);

  // Silent audio ref for iOS background keep-alive
  const silentAudioRef = useRef(null);

  // Start silent audio to keep iOS app alive in background
  const startSilentAudio = useCallback(() => {
    try {
      if (silentAudioRef.current) return; // Already playing
      
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        console.log('AudioContext not supported');
        return;
      }
      
      const audioCtx = new AudioContext();
      
      // Create a silent oscillator
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      // Set gain to nearly zero (silent but not completely - iOS might optimize it away)
      gainNode.gain.value = 0.001;
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      // Very low frequency, inaudible
      oscillator.frequency.value = 1;
      oscillator.start();
      
      silentAudioRef.current = { audioCtx, oscillator, gainNode };
      console.log('Silent audio started for iOS background keep-alive');
    } catch (err) {
      console.error('Failed to start silent audio:', err);
    }
  }, []);

  // Stop silent audio
  const stopSilentAudio = useCallback(() => {
    if (silentAudioRef.current) {
      try {
        silentAudioRef.current.oscillator.stop();
        silentAudioRef.current.audioCtx.close();
      } catch (err) {
        console.error('Error stopping silent audio:', err);
      }
      silentAudioRef.current = null;
      console.log('Silent audio stopped');
    }
  }, []);

  // Show persistent notification for location sharing (helps keep app alive in background)
  const showLocationSharingNotification = useCallback(async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Check if we have notification permission
      if (Notification.permission === 'granted') {
        navigator.serviceWorker.controller.postMessage({
          type: 'SHOW_LOCATION_SHARING_NOTIFICATION',
          message: 'Your location is being shared with dispatch'
        });
        console.log('Requested persistent location notification');
      } else if (Notification.permission === 'default') {
        // Request permission
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          navigator.serviceWorker.controller.postMessage({
            type: 'SHOW_LOCATION_SHARING_NOTIFICATION',
            message: 'Your location is being shared with dispatch'
          });
        }
      }
    }
  }, []);

  // Hide persistent notification for location sharing
  const hideLocationSharingNotification = useCallback(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'HIDE_LOCATION_SHARING_NOTIFICATION'
      });
      console.log('Requested to hide location notification');
    }
  }, []);

  // ===== USER-INITIATED LOCATION SHARING (persists across navigation) =====
  
  // Send user location to backend
  const sendUserLocation = useCallback(async (latitude, longitude, accuracy, callId) => {
    try {
      const response = await axios.post(`${API}/location/share`, {
        latitude,
        longitude,
        accuracy,
        live_call_id: callId,
        is_active: true,
        visibility: 'public'
      }, { withCredentials: true });
      
      // Check if backend told us to stop (call was logged)
      if (response.data.should_stop) {
        console.log('Backend says stop user sharing - call was logged');
        stopUserLocationSharing();
        toast.info("Location sharing stopped - call has been logged as incident");
      }
    } catch (err) {
      console.error('Failed to send user location:', err);
    }
  }, []);

  // Start user-initiated location sharing
  const startUserLocationSharing = useCallback(async (callId = null) => {
    if (userLocationWatchRef.current !== null) {
      console.log('User location sharing already active');
      return { success: true, alreadySharing: true };
    }

    if (!('geolocation' in navigator)) {
      toast.error('Geolocation is not supported on this device');
      return { success: false, error: 'not_supported' };
    }

    try {
      toast.info("Getting your location... This may take 10-30 seconds.");
      
      // Get initial position
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0
        });
      });

      console.log('User location sharing - got initial position:', position.coords);
      
      // Send initial location
      await sendUserLocation(
        position.coords.latitude,
        position.coords.longitude,
        position.coords.accuracy,
        callId
      );

      // Update state
      setUserLocationSharing(true);
      setUserSharingCallId(callId);
      setUserSharingAccuracy(Math.round(position.coords.accuracy));
      userLastPositionRef.current = position;

      // Start watching
      userLocationWatchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          userLastPositionRef.current = pos;
          setUserSharingAccuracy(Math.round(pos.coords.accuracy));
          
          const now = Date.now();
          if (now - userLastSendTimeRef.current >= LOCATION_UPDATE_INTERVAL) {
            userLastSendTimeRef.current = now;
            sendUserLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, callId);
          }
        },
        (error) => {
          console.error('User geolocation watch error:', error);
        },
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 5000 }
      );

      // Backup interval
      userLocationIntervalRef.current = setInterval(() => {
        if (userLastPositionRef.current && userLocationSharingRef.current) {
          sendUserLocation(
            userLastPositionRef.current.coords.latitude,
            userLastPositionRef.current.coords.longitude,
            userLastPositionRef.current.coords.accuracy,
            callId
          );
          
          // Also ping service worker to keep it alive
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'LOCATION_UPDATE',
              coords: {
                latitude: userLastPositionRef.current.coords.latitude,
                longitude: userLastPositionRef.current.coords.longitude
              }
            });
          }
        }
      }, 5000);

      // Request wake lock to help keep location running in background
      requestWakeLock();
      
      // Start silent audio to keep iOS alive in background
      startSilentAudio();
      
      // Show persistent notification to help keep app alive in background
      showLocationSharingNotification();

      toast.success("Location sharing started");
      return { success: true };
    } catch (error) {
      console.error('Failed to start user location sharing:', error);
      if (error.code === error.PERMISSION_DENIED) {
        toast.error("Location permission denied. Please enable location access.");
        return { success: false, error: 'permission_denied' };
      }
      toast.error("Failed to get location: " + error.message);
      return { success: false, error: error.message };
    }
  }, [sendUserLocation, requestWakeLock, startSilentAudio, showLocationSharingNotification]);

  // Stop user-initiated location sharing
  const stopUserLocationSharing = useCallback(async () => {
    console.log('Stopping user location sharing');
    
    // Release wake lock
    releaseWakeLock();
    
    // Stop silent audio
    stopSilentAudio();
    
    // Hide persistent notification
    hideLocationSharingNotification();
    
    // Clear watcher
    if (userLocationWatchRef.current !== null) {
      navigator.geolocation.clearWatch(userLocationWatchRef.current);
      userLocationWatchRef.current = null;
    }
    
    // Clear interval
    if (userLocationIntervalRef.current) {
      clearInterval(userLocationIntervalRef.current);
      userLocationIntervalRef.current = null;
    }

    // Update state
    setUserLocationSharing(false);
    setUserSharingCallId(null);
    setUserSharingAccuracy(null);
    userLastPositionRef.current = null;

    // Tell backend to stop
    try {
      await axios.post(`${API}/location/share`, {
        latitude: 0,
        longitude: 0,
        is_active: false
      }, { withCredentials: true });
      console.log('User location sharing stopped on backend');
      toast.success("Location sharing stopped");
    } catch (err) {
      console.error('Failed to stop user location sharing on backend:', err);
      toast.error("Failed to stop location on server - please try again");
    }
  }, [releaseWakeLock, stopSilentAudio, hideLocationSharingNotification]);

  // Check and restore user location sharing on mount/page visibility
  const checkAndRestoreUserLocationSharing = useCallback(async (forceRestart = false) => {
    try {
      const response = await axios.get(`${API}/location/my-status`, { withCredentials: true });
      const { is_sharing, location } = response.data;
      
      if (is_sharing && location) {
        const callId = location.live_call_id || null;
        
        // If force restart OR no watcher exists, set up the watcher
        if (forceRestart || !userLocationWatchRef.current) {
          console.log('Restoring user location sharing for call:', callId, forceRestart ? '(forced)' : '');
          
          // Clear existing watcher if force restarting
          if (forceRestart && userLocationWatchRef.current) {
            navigator.geolocation.clearWatch(userLocationWatchRef.current);
            userLocationWatchRef.current = null;
          }
          
          // Update state
          setUserLocationSharing(true);
          setUserSharingCallId(callId);
          
          // Immediately get current position and send it (don't wait for watcher)
          try {
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
              });
            });
            console.log('Got immediate position on restore:', position.coords.latitude, position.coords.longitude);
            userLastPositionRef.current = position;
            setUserSharingAccuracy(Math.round(position.coords.accuracy));
            sendUserLocation(position.coords.latitude, position.coords.longitude, position.coords.accuracy, callId);
          } catch (err) {
            console.error('Failed to get immediate position:', err);
          }
          
          // Start watching
          userLocationWatchRef.current = navigator.geolocation.watchPosition(
            (pos) => {
              userLastPositionRef.current = pos;
              setUserSharingAccuracy(Math.round(pos.coords.accuracy));
              
              const now = Date.now();
              if (now - userLastSendTimeRef.current >= LOCATION_UPDATE_INTERVAL) {
                userLastSendTimeRef.current = now;
                sendUserLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, callId);
              }
            },
            (error) => console.error('Geolocation error:', error),
            { enableHighAccuracy: true, timeout: 30000, maximumAge: 5000 }
          );
          
          // Restart backup interval
          if (userLocationIntervalRef.current) {
            clearInterval(userLocationIntervalRef.current);
          }
          userLocationIntervalRef.current = setInterval(() => {
            if (userLastPositionRef.current && userLocationSharingRef.current) {
              sendUserLocation(
                userLastPositionRef.current.coords.latitude,
                userLastPositionRef.current.coords.longitude,
                userLastPositionRef.current.coords.accuracy,
                callId
              );
            }
          }, 5000);
          
          // Re-request wake lock
          requestWakeLock();
          
          // Re-start silent audio
          startSilentAudio();
          
          // Also show notification
          showLocationSharingNotification();
        }
      } else if (!is_sharing && userLocationSharingRef.current) {
        // Backend says not sharing but we think we are - sync state
        console.log('Backend says not sharing, clearing local state');
        if (userLocationWatchRef.current) {
          navigator.geolocation.clearWatch(userLocationWatchRef.current);
          userLocationWatchRef.current = null;
        }
        if (userLocationIntervalRef.current) {
          clearInterval(userLocationIntervalRef.current);
          userLocationIntervalRef.current = null;
        }
        setUserLocationSharing(false);
        setUserSharingCallId(null);
        setUserSharingAccuracy(null);
        releaseWakeLock();
        stopSilentAudio();
        hideLocationSharingNotification();
      }
    } catch (error) {
      console.error('Failed to check user location sharing status:', error);
    }
  }, [sendUserLocation, requestWakeLock, startSilentAudio, showLocationSharingNotification, releaseWakeLock, stopSilentAudio, hideLocationSharingNotification]);

  // Restore user location sharing on mount and visibility change
  useEffect(() => {
    checkAndRestoreUserLocationSharing(false); // Don't force on mount
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Force restart watcher when returning to foreground
        // This handles the case where the watcher was suspended by the OS
        if (userLocationSharingRef.current) {
          console.log('App returned to foreground while sharing - force restarting watcher');
          checkAndRestoreUserLocationSharing(true);
        } else {
          checkAndRestoreUserLocationSharing(false);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also handle page focus (some browsers use this instead of/in addition to visibility)
    const handleFocus = () => {
      if (userLocationSharingRef.current) {
        console.log('Window focused while sharing - checking location');
        // Just send current position if we have it, don't fully restart
        if (userLastPositionRef.current) {
          sendUserLocation(
            userLastPositionRef.current.coords.latitude,
            userLastPositionRef.current.coords.longitude,
            userLastPositionRef.current.coords.accuracy,
            userSharingCallId
          );
        }
      }
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkAndRestoreUserLocationSharing, sendUserLocation, userSharingCallId]);

  // Listen for callLogged event to stop user sharing
  useEffect(() => {
    const handleCallLogged = (event) => {
      const { callId } = event.detail;
      if (userSharingCallId && callId === userSharingCallId && userLocationSharingRef.current) {
        console.log('Call logged, stopping user location sharing for call:', callId);
        stopUserLocationSharing();
        toast.info("Location sharing stopped - call has been logged as incident");
      }
    };

    window.addEventListener('callLogged', handleCallLogged);
    return () => window.removeEventListener('callLogged', handleCallLogged);
  }, [userSharingCallId, stopUserLocationSharing]);

  // Listen for service worker messages (stop sharing, force refresh)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleSWMessage = (event) => {
        if (event.data && event.data.type === 'STOP_LOCATION_SHARING') {
          console.log('Received stop sharing command from service worker');
          if (userLocationSharingRef.current) {
            stopUserLocationSharing();
          }
        }
        if (event.data && event.data.type === 'FORCE_REFRESH') {
          console.log('Received force refresh command from service worker');
          // Clear the marker first — we're about to satisfy it, and leaving it
          // set would cost the fresh page a second reload on boot.
          takePendingRefresh().finally(() => {
            closeRefreshNotifications().finally(() => window.location.reload());
          });
        }
      };

      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      return () => navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    }
  }, [stopUserLocationSharing]);

  // A refresh pushed while the app was asleep can't be delivered by message —
  // on iOS there's no live window to receive it. The service worker leaves a
  // marker instead; pick it up on the way in and whenever we return to the
  // foreground. takePendingRefresh() clears the marker as it reads it, so this
  // reloads once and doesn't loop.
  useEffect(() => {
    let cancelled = false;

    const consumePendingRefresh = async () => {
      const owed = await takePendingRefresh();
      if (cancelled) return;
      if (owed) {
        console.log('Applying refresh requested while the app was closed');
        await closeRefreshNotifications();
        window.location.reload();
        return;
      }
      // Nothing owed, so any refresh notice still on screen is stale. iOS gets
      // one on every refresh even when the app is open and reloads instantly
      // via postMessage, and the service worker can finish showing it after
      // that reload already dismissed it — leaving it stranded otherwise.
      closeRefreshNotifications();
    };

    consumePendingRefresh();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') consumePendingRefresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // ===== END USER-INITIATED LOCATION SHARING =====

  // Check if unit has tracking enabled and start/stop location sharing
  const checkAndUpdateLocationTracking = useCallback(async () => {
    if (!user?.linked_unit) return;
    
    try {
      const response = await axios.get(`${API}/location/my-status`, { withCredentials: true });
      const { unit_tracking } = response.data;
      
      const shouldBeTracking = unit_tracking?.is_active === true;
      const currentlyTracking = isLocationTrackingRef.current;

      if (shouldBeTracking && !currentlyTracking) {
        // Start tracking
        console.log('Starting location tracking for call:', unit_tracking.call_id);
        setIsLocationTracking(true);
        setTrackingCallAddress(unit_tracking.call_address);
        setTrackingCallId(unit_tracking.call_id);
      } else if (!shouldBeTracking && currentlyTracking) {
        // Stop tracking
        console.log('Stopping location tracking');
        setIsLocationTracking(false);
        setTrackingCallAddress(null);
        setTrackingCallId(null);
        
        // Stop watching location
        if (locationWatchIdRef.current) {
          navigator.geolocation.clearWatch(locationWatchIdRef.current);
          locationWatchIdRef.current = null;
          console.log('Cleared geolocation watcher');
        }
        
        // Tell server to stop sharing
        try {
          await axios.post(`${API}/location/share`, {
            latitude: 0,
            longitude: 0,
            is_active: false
          }, { withCredentials: true });
          console.log('Sent stop sharing request to server');
        } catch (err) {
          console.error('Failed to stop location sharing:', err);
        }
      }
    } catch (error) {
      console.error('Failed to check location tracking status:', error);
    }
  }, [user?.linked_unit]); // Remove isLocationTracking from deps, use ref instead

  // Start geolocation watcher when tracking becomes active
  useEffect(() => {
    if (isLocationTracking && trackingCallId && !locationWatchIdRef.current) {
      console.log('Requesting geolocation permission...');
      
      if (!('geolocation' in navigator)) {
        console.error('Geolocation not supported');
        toast.error('Geolocation is not supported on this device');
        return;
      }
      
      // First get current position to trigger permission prompt
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('Got initial position, starting watcher');
          
          // Send initial position
          axios.post(`${API}/location/share`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            live_call_id: trackingCallId,
            is_active: true,
            visibility: 'public'
          }, { withCredentials: true }).catch(err => console.error('Failed to share initial location:', err));
          
          // Start watching for updates
          locationWatchIdRef.current = navigator.geolocation.watchPosition(
            async (pos) => {
              // Throttle updates to avoid spamming the server
              const now = Date.now();
              if (now - lastLocationUpdateRef.current < LOCATION_UPDATE_INTERVAL) {
                return; // Skip this update, too soon
              }
              lastLocationUpdateRef.current = now;
              
              console.log('Location update:', pos.coords.latitude, pos.coords.longitude);
              try {
                await axios.post(`${API}/location/share`, {
                  latitude: pos.coords.latitude,
                  longitude: pos.coords.longitude,
                  accuracy: pos.coords.accuracy,
                  live_call_id: trackingCallId,
                  is_active: true,
                  visibility: 'public'
                }, { withCredentials: true });
              } catch (err) {
                console.error('Failed to share location:', err);
              }
            },
            (error) => {
              console.error('Geolocation watch error:', error);
              toast.error('Location error: ' + error.message);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
          );
        },
        (error) => {
          console.error('Geolocation permission error:', error);
          toast.error('Location permission denied. Please enable location access.');
          setIsLocationTracking(false);
          setTrackingCallId(null);
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    }
    
    return () => {
      if (locationWatchIdRef.current && !isLocationTracking) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
        locationWatchIdRef.current = null;
      }
    };
  }, [isLocationTracking, trackingCallId]);

  // Listen for callLogged event to immediately stop tracking
  useEffect(() => {
    const handleCallLogged = async (event) => {
      const { callId } = event.detail;
      console.log('Call logged event received:', callId);
      
      // If currently doing unit-based tracking for this call, stop immediately
      if (trackingCallId && callId === trackingCallId && isLocationTracking) {
        console.log('Call logged, stopping unit location tracking for call:', callId);
        setIsLocationTracking(false);
        setTrackingCallAddress(null);
        setTrackingCallId(null);
        
        if (locationWatchIdRef.current) {
          navigator.geolocation.clearWatch(locationWatchIdRef.current);
          locationWatchIdRef.current = null;
        }
        
        toast.info("Location tracking stopped - call has been logged");
      }
      
      // Always tell server to stop sharing for this user
      // This handles both unit-based and user-initiated location sharing
      try {
        await axios.post(`${API}/location/share`, {
          latitude: 0,
          longitude: 0,
          is_active: false
        }, { withCredentials: true });
        console.log('Sent stop sharing request for logged call');
      } catch (err) {
        console.error('Failed to stop location sharing:', err);
      }
    };

    window.addEventListener('callLogged', handleCallLogged);
    return () => {
      window.removeEventListener('callLogged', handleCallLogged);
    };
  }, [trackingCallId, isLocationTracking]);

  // Check tracking status periodically
  useEffect(() => {
    if (user?.linked_unit) {
      // Initial check
      checkAndUpdateLocationTracking();
      
      // Check every 30 seconds (reduced from 10 to prevent excessive API calls)
      trackingCheckIntervalRef.current = setInterval(checkAndUpdateLocationTracking, 30000);
      
      return () => {
        if (trackingCheckIntervalRef.current) {
          clearInterval(trackingCheckIntervalRef.current);
        }
        if (locationWatchIdRef.current) {
          navigator.geolocation.clearWatch(locationWatchIdRef.current);
        }
      };
    }
  }, [user?.linked_unit, checkAndUpdateLocationTracking]);

  // Check for app updates
  const checkForUpdates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/version`, { timeout: 5000 });
      const serverVersion = response.data.version;
      
      if (serverVersion && serverVersion !== CLIENT_VERSION) {
        console.log(`Update available: ${CLIENT_VERSION} -> ${serverVersion}`);
        setUpdateAvailable(true);
        
        // Show toast only once per session
        if (!hasShownUpdateToast.current) {
          hasShownUpdateToast.current = true;
          toast.info(
            <div className="flex flex-col gap-2">
              <span className="font-semibold">Update Available</span>
              <span className="text-sm">A new version is available. Tap to refresh.</span>
            </div>,
            {
              duration: Infinity,
              action: {
                label: "Refresh Now",
                onClick: () => {
                  // Clear caches and reload
                  if ('caches' in window) {
                    caches.keys().then(names => {
                      names.forEach(name => caches.delete(name));
                    });
                  }
                  // Update service worker
                  if ('serviceWorker' in navigator) {
                    navigator.serviceWorker.getRegistrations().then(registrations => {
                      registrations.forEach(registration => {
                        registration.update();
                      });
                    });
                  }
                  // Force reload from server
                  window.location.reload(true);
                }
              }
            }
          );
        }
      }
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.log('Version check failed:', error.message);
    }
  }, []);

  // Check for updates on mount and periodically
  useEffect(() => {
    // Initial check after 5 seconds
    const initialCheck = setTimeout(() => {
      checkForUpdates();
    }, 5000);

    // Check every 5 minutes
    updateCheckRef.current = setInterval(() => {
      checkForUpdates();
    }, 5 * 60 * 1000);

    // Also check when app becomes visible (user returns to tab/app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialCheck);
      if (updateCheckRef.current) {
        clearInterval(updateCheckRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdates]);

  const toggleTheme = () => {
    const allowed = user?.allowed_themes || (user?.role === 'admin' ? ['dark', 'light', 'tactical'] : ['light', 'tactical']);
    const themeOrder = ['dark', 'light', 'tactical'].filter(t => allowed.includes(t));
    if (themeOrder.length === 0) themeOrder.push('light');
    const currentIdx = themeOrder.indexOf(theme);
    const nextTheme = themeOrder[(currentIdx >= 0 ? currentIdx + 1 : 0) % themeOrder.length];
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
    document.documentElement.classList.remove('light', 'dark', 'tactical');
    document.documentElement.classList.add(nextTheme);
    if (nextTheme === 'tactical') document.documentElement.classList.add('dark');
    // Keep CAD class if active
    if (cadMode) document.documentElement.classList.add('cad');
    const themeColors = { light: '#f8fafc', tactical: '#162040', dark: '#0f172a' };
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.setAttribute('content', cadMode ? '#0a0f1a' : (themeColors[nextTheme] || '#0f172a'));
    const schemeMeta = document.querySelector('meta[name="color-scheme"]');
    if (schemeMeta) schemeMeta.setAttribute('content', nextTheme === 'light' ? 'light' : 'dark');
  };

  const toggleCadMode = () => {
    const next = !cadMode;
    setCadMode(next);
    localStorage.setItem('cadMode', next ? 'true' : 'false');
    if (next) {
      document.documentElement.classList.add('cad');
    } else {
      document.documentElement.classList.remove('cad');
    }
  };

  // Font size classes - 4 sizes that cycle
  const fontSizes = ['small', 'normal', 'large', 'xlarge'];

  useEffect(() => {
    document.documentElement.classList.remove('font-small', 'font-normal', 'font-large', 'font-xlarge');
    document.documentElement.classList.add(`font-${fontSize}`);
  }, [fontSize]);

  const cycleFontSize = () => {
    const currentIndex = fontSizes.indexOf(fontSize);
    const nextIndex = (currentIndex + 1) % fontSizes.length;
    const newSize = fontSizes[nextIndex];
    setFontSize(newSize);
    localStorage.setItem('fontSize', newSize);
  };

  const handleNotificationClick = () => {
    navigate('/notification-settings');
  };

  // Check if push notifications are active on login - prompt if not
  // Check if push notifications are active - prompt if not (every login)
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const notificationCheckDone = useRef(false);
  useEffect(() => {
    const checkNotificationStatus = async () => {
      if (notificationCheckDone.current) return;
      if (!user) return;
      if (window.location.pathname === '/notification-settings') return;

      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        if (Notification.permission === 'denied') return;

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
          try {
            const res = await axios.get(`${API}/push/status`, { withCredentials: true });
            if (!res.data.is_subscribed) {
              setShowNotificationPrompt(true);
              notificationCheckDone.current = true;
            }
          } catch {
            // Silently fail
          }
        } else {
          notificationCheckDone.current = true;
        }
      } catch (err) {
        console.error('Error checking notification status:', err);
      }
    };

    const timer = setTimeout(checkNotificationStatus, 3000);
    return () => clearTimeout(timer);
  }, [user]);

  return (
    <SettingsContext.Provider value={{ isDark, theme, toggleTheme, cadMode, toggleCadMode, fontSize, setFontSize }}>
      <LocationSharingContext.Provider value={{
        isSharing: userLocationSharing,
        sharingCallId: userSharingCallId,
        accuracy: userSharingAccuracy,
        startSharing: startUserLocationSharing,
        stopSharing: stopUserLocationSharing
      }}>
        <SipPhoneContext.Provider value={{
          dialNumber: setSipPhoneNumber,
          handlePhoneClick
        }}>
          <div className={`app-container flex ${theme === 'light' ? 'light' : theme === 'tactical' ? 'tactical' : ''}`}>
            {/* Location Tracking Banner - show for either unit-based or user-initiated */}
            {(isLocationTracking || userLocationSharing) && (
              <div
                className="fixed top-0 left-0 right-0 bg-emerald-600 text-white text-center py-2 px-4 z-[100] flex items-center justify-center gap-2"
              >
                <span className="text-sm font-medium">📍 Sharing location{trackingCallAddress ? ` for: ${trackingCallAddress}` : ''}</span>
              </div>
            )}

            {/* Update Banner */}
            {updateAvailable && !isLocationTracking && !userLocationSharing && (
              <div
                className="fixed top-0 left-0 right-0 bg-blue-600 text-white text-center py-2 px-4 z-[100] cursor-pointer flex items-center justify-center gap-2"
                onClick={() => {
                  if ('caches' in window) {
                    caches.keys().then(names => names.forEach(name => caches.delete(name)));
                  }
                  window.location.reload(true);
                }}
              >
                <span className="text-sm font-medium">🔄 Update available - Tap to refresh</span>
              </div>
            )}

            {/* Notification Enable Prompt - centered modal */}
            {showNotificationPrompt && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xl p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mx-auto mb-4">
                    <Bell className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Notifications Are Off</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    You're not receiving push notifications. Enable them in notification settings to stay updated with live calls and alerts.
                  </p>
                  <div className="flex flex-col gap-2 mt-5">
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        setShowNotificationPrompt(false);
                        navigate('/notification-settings');
                      }}
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Go to Notification Settings
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full text-slate-500 dark:text-slate-400"
                      onClick={() => setShowNotificationPrompt(false)}
                    >
                      Remind Me Later
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Sidebar Navigation */}
            <Sidebar
              user={user}
              onLogout={logout}
              isDark={isDark}
              theme={theme}
              onToggleTheme={toggleTheme}
              cadMode={cadMode}
              onToggleCadMode={toggleCadMode}
              notificationCount={notificationCount}
              onNotificationClick={handleNotificationClick}
              fontSize={fontSize}
              onCycleFontSize={cycleFontSize}
              appLockEnabled={!!user?.app_lock_enabled}
              phoneWidgetEnabled={!!user?.phone_widget_enabled}
              phoneRegistered={sipRegistered}
              phoneConnecting={sipConnecting}
              phoneCallActive={sipCallActive}
              onExpandDialer={() => sipPhoneRef.current?.expand()}
            />

            {/* Main Content */}
            <main className={`flex-1 ${updateAvailable || isLocationTracking || userLocationSharing ? 'mt-8' : ''}`} style={{ overscrollBehavior: 'none', overflow: 'auto', paddingTop: (updateAvailable || isLocationTracking || userLocationSharing) ? 'calc(var(--navbar-height, 56px) + 32px)' : 'var(--navbar-height, 56px)' }}>
              <div className="px-4 pb-4 pt-4 md:p-6 min-h-full">
                {children}
              </div>
            </main>

            {/* SIP Phone Widget - only show if enabled for user */}
            {user?.phone_widget_enabled && (
              <SipPhone
                ref={sipPhoneRef}
                initialNumber={sipPhoneNumber}
                onCallStateChange={(state) => setSipCallActive(state !== "idle")}
                onStatusChange={({ isRegistered, isConnecting }) => {
                  setSipRegistered(isRegistered);
                  setSipConnecting(isConnecting);
                }}
                onCallMyPhone={(number) => {
                  setCallMyPhoneConfirmNumber(number);
                }}
                hasPhoneNumber={!!user?.phone_number}
              />
            )}

            {/* Incoming Call Alert Panel for Dispatchers */}
            <IncomingCallAlert />

            {/* Call Method Dialog */}
            <CallMethodDialog
              open={showCallMethodDialog}
              onOpenChange={setShowCallMethodDialog}
              phoneNumber={pendingCallNumber}
              onChooseNative={handleNativeDialer}
              onChooseDispatch={handleDispatchPhone}
              onChooseCallMyPhone={handleCallMyPhone}
              hasPhoneNumber={!!user?.phone_number}
            />

            {/* Call My Phone Confirmation Dialog */}
            {callMyPhoneConfirmNumber && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setCallMyPhoneConfirmNumber("")}>
                <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-purple-600/20 flex items-center justify-center mx-auto mb-3">
                      <Bell className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Call My Phone</h3>
                    {user?.phone_number && (
                      <p className="text-sm font-mono text-purple-500 dark:text-purple-400 mt-2">
                        {user.phone_number}
                      </p>
                    )}
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                      The app will ring your cell phone first. When you answer, it will connect you to the destination.
                    </p>
                    <p className="text-sm text-amber-500 dark:text-amber-400 mt-2 font-medium">
                      Make sure you have good cell coverage before proceeding, otherwise the caller will only hear your voicemail.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <button
                      onClick={executeCallMyPhone}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors"
                    >
                      Proceed
                    </button>
                    <button
                      onClick={() => setCallMyPhoneConfirmNumber("")}
                      className="w-full px-4 py-2.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Global Watermark Overlay - Screenshot Protection */}
            <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden select-none" aria-hidden="true">
              {/* Centered App Logo Watermark */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative flex items-center justify-center">
                  <img
                    src="/CHSP_PR_2026.png"
                    alt=""
                    className="w-80 h-80 md:w-[500px] md:h-[500px] object-contain opacity-[0.03] dark:opacity-[0.015]"
                    draggable="false"
                  />
                  <span className="absolute -translate-y-10 md:-translate-y-14 text-sm md:text-lg font-semibold text-slate-900/[0.05] dark:text-slate-100/[0.03] whitespace-nowrap select-none">
                    {user?.name || user?.email || ''}
                  </span>
                </div>
              </div>
              {/* Username Watermark - Dense grid coverage for screenshot protection */}
              <div className="absolute inset-0">
                {/* Row 1 - Top */}
                <div className="absolute top-8 left-4 md:top-6 md:left-8 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.08] dark:text-slate-400/[0.04] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-12 left-1/3 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-8 right-1/3 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-14 right-4 md:top-10 md:right-12 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.08] dark:text-slate-400/[0.04] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>

                {/* Row 2 - Upper middle */}
                <div className="absolute top-[20%] left-8 md:left-16 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-[22%] left-[45%] transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.05] dark:text-slate-400/[0.025] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-[18%] right-12 md:right-20 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>

                {/* Row 3 - Center zone */}
                <div className="absolute top-1/3 left-4 md:left-12 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.07] dark:text-slate-400/[0.035] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-[35%] left-1/4 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.05] dark:text-slate-400/[0.025] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-1/3 right-1/4 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.05] dark:text-slate-400/[0.025] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-[32%] right-4 md:right-10 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.07] dark:text-slate-400/[0.035] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>

                {/* Row 4 - True center */}
                <div className="absolute top-1/2 left-8 md:left-20 transform -translate-y-1/2 rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-1/2 right-8 md:right-20 transform -translate-y-1/2 rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>

                {/* Row 5 - Lower center */}
                <div className="absolute top-[60%] left-4 md:left-12 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.07] dark:text-slate-400/[0.035] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-[58%] left-1/3 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.05] dark:text-slate-400/[0.025] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-[62%] right-1/3 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.05] dark:text-slate-400/[0.025] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute top-[60%] right-4 md:right-10 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.07] dark:text-slate-400/[0.035] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>

                {/* Row 6 - Lower zone */}
                <div className="absolute bottom-[25%] left-8 md:left-16 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute bottom-[28%] left-1/2 transform -translate-x-1/2 rotate-[-25deg]">
                  <span className="text-slate-700/[0.05] dark:text-slate-400/[0.025] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute bottom-[25%] right-8 md:right-16 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>

                {/* Row 7 - Bottom */}
                <div className="absolute bottom-16 left-4 md:bottom-12 md:left-8 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.08] dark:text-slate-400/[0.04] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute bottom-20 left-1/3 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute bottom-16 right-1/3 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.06] dark:text-slate-400/[0.03] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
                <div className="absolute bottom-12 right-4 md:bottom-8 md:right-8 transform rotate-[-25deg]">
                  <span className="text-slate-700/[0.08] dark:text-slate-400/[0.04] text-[9px] md:text-xs font-medium whitespace-nowrap">
                    {user?.name || user?.email || 'User'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </SipPhoneContext.Provider>
      </LocationSharingContext.Provider>
    </SettingsContext.Provider>
  );
};

// Router Component
const AppRouter = () => {
  const location = useLocation();
  const appNavigate = useNavigate();

  // Global keyboard shortcut: F2 → open dispatch new call form
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        // Don't trigger when typing in an input field
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select" || document.activeElement?.isContentEditable) return;
        e.preventDefault();
        appNavigate("/live-calls?new=1");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [appNavigate]);

  // Check for OAuth callback (Google sends code in query params)
  if (location.pathname === "/auth/callback" && location.search?.includes("code=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/login" element={<Login />} />
      <Route path="/member-dashboard" element={
        <ProtectedRoute>
          <MainLayout><MemberDashboard /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout><Dashboard /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/live-calls" element={
        <ProtectedRoute>
          <MainLayout><LiveCalls /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/response" element={
        <ProtectedRoute>
          <MainLayout><Response /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/incidents" element={
        <ProtectedRoute>
          <MainLayout><Incidents /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/incidents/:incidentId" element={
        <ProtectedRoute>
          <MainLayout><IncidentDetail /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/investigations" element={
        <ProtectedRoute>
          <MainLayout><Investigations /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/call-history" element={
        <ProtectedRoute>
          <MainLayout><CallHistory /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/building-codes" element={
        <ProtectedRoute>
          <MainLayout><BuildingCodes /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/patrol" element={
        <ProtectedRoute>
          <MainLayout><Patrol /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/patrol-admin" element={<Navigate to="/patrol" replace />} />
      <Route path="/rollcall" element={
        <ProtectedRoute>
          <MainLayout><RollCall /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/units" element={
        <ProtectedRoute>
          <MainLayout><Units /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/dispatch" element={
        <ProtectedRoute>
          <MainLayout><Dispatch /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/perpetrators" element={
        <ProtectedRoute>
          <MainLayout><ProfilesAdmin /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/perpetrators/:profileId" element={
        <ProtectedRoute>
          <MainLayout><ProfilesAdmin /></MainLayout>
        </ProtectedRoute>
      } />
      {/* Redirect old profiles-admin route to perpetrators */}
      <Route path="/profiles-admin" element={<Navigate to="/perpetrators" replace />} />
      <Route path="/profiles-admin/:profileId" element={<Navigate to="/perpetrators" replace />} />
      <Route path="/pr" element={
        <ProtectedRoute>
          <MainLayout><PR /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requireAdmin>
          <MainLayout><SettingsPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/notification-settings" element={
        <ProtectedRoute>
          <MainLayout><NotificationSettings /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/notification-history" element={
        <ProtectedRoute requiredPage="notification_history">
          <MainLayout><NotificationHistory /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/media" element={
        <ProtectedRoute>
          <MainLayout><Media /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/watermarking" element={
        <ProtectedRoute requiredPage="watermarking">
          <MainLayout><Watermarking /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/radio" element={
        <ProtectedRoute requiredPage="radio">
          <MainLayout><RadioRecordings /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/hotline" element={
        <ProtectedRoute requiredPage="hotline">
          <MainLayout><Hotline /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/map" element={
        <ProtectedRoute>
          <MainLayout><Map /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/event-ops" element={
        <ProtectedRoute>
          <MainLayout><EventOps /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
};

// Main App Component
// Shared data cache - fetches once, returns cached on subsequent calls
const sharedDataCache = {};
const sharedDataPromises = {};

const SharedDataProvider = ({ children }) => {
  const fetchCached = useCallback(async (key, url, maxAgeMs = 60000) => {
    const now = Date.now();
    const cached = sharedDataCache[key];
    if (cached && (now - cached.time) < maxAgeMs) {
      return cached.data;
    }
    // Deduplicate in-flight requests
    if (sharedDataPromises[key]) {
      return sharedDataPromises[key];
    }
    sharedDataPromises[key] = axios.get(`${API}${url}`, { withCredentials: true, timeout: 10000 })
      .then(res => {
        sharedDataCache[key] = { data: res.data, time: Date.now() };
        delete sharedDataPromises[key];
        return res.data;
      })
      .catch(err => {
        delete sharedDataPromises[key];
        if (cached) return cached.data; // Return stale on error
        throw err;
      });
    return sharedDataPromises[key];
  }, []);

  const invalidate = useCallback((key) => {
    delete sharedDataCache[key];
  }, []);

  // Convenience fetchers with appropriate cache times
  const getUnits = useCallback(() => fetchCached("units", "/units", 120000), [fetchCached]); // 2min
  const getIncidentTypes = useCallback(() => fetchCached("incidentTypes", "/incident-types", 120000), [fetchCached]);
  const getProfiles = useCallback(() => fetchCached("profiles", "/perpetrator-profiles", 60000), [fetchCached]); // 1min
  const getRadioCodes = useCallback(() => fetchCached("radioCodes", "/app-config/radio-codes", 300000), [fetchCached]); // 5min
  const getCallFinals = useCallback(() => fetchCached("callFinals", "/app-config/call-finals", 300000), [fetchCached]);
  const getPatrolCars = useCallback(() => fetchCached("patrolCars", "/patrol/cars", 120000), [fetchCached]);
  const getWaBotGroups = useCallback(() => fetchCached("waBotGroups", "/audit/whatsapp/bot-groups", 300000), [fetchCached]); // 5min - external service
  const getWaConfig = useCallback(() => fetchCached("waConfig", "/audit/whatsapp/config", 300000), [fetchCached]); // 5min

  return (
    <SharedDataContext.Provider value={{
      fetchCached, invalidate,
      getUnits, getIncidentTypes, getProfiles, getRadioCodes,
      getCallFinals, getPatrolCars, getWaBotGroups, getWaConfig
    }}>
      {children}
    </SharedDataContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SharedDataProvider>
          <Toaster
            position="top-right"
            richColors
            theme="dark"
            toastOptions={{
              className: "bg-slate-900 border-slate-800 text-slate-200"
            }}
          />
          <AppRouter />
        </SharedDataProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, getAuthenticatedMediaUrl };
